import { Injectable, inject } from '@angular/core';
import { httpsCallable } from 'firebase/functions';
import { FUNCTIONS } from 'src/app/common/firebase/firebase.tokens';
import { ElasticService } from './elastic.service';
import { EnrollhereApiService } from '../../common/enrollhere-api/enrollhere-api';

export interface UserSearchParams {
  query: string;
  size?: number;
  userType?: string[];
  agencyId?: string;
}

export interface UserSearchResult {
  id: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  npn?: string;
}

interface UserSearchResponse {
  users: UserSearchResult[];
  total: number;
  from: number;
  size: number;
}

@Injectable({
  providedIn: 'root',
})
export class SearchService {
  private elasticService = inject(ElasticService);
  /** @deprecated Direct Firebase Functions access is deprecated. Use API endpoints instead. */
  private functions = inject(FUNCTIONS);
  private apiService = inject(EnrollhereApiService);

  async autocomplete(
    term: string,
    index: string,
    fields: string[],
    locationBased?: any,
    getFullSource = false,
    useNewQuery = false,
    preload = false,
    filterByAgency?: string,
    size = 25,
    filterIds?: string[],
    filterByTags?: string[]
  ) {
    console.log('autocomplete', {
      term,
      index,
      fields,
      locationBased,
      getFullSource,
      useNewQuery,
      preload,
      filterByAgency,
      filterIds,
      filterByTags,
    });

    let query: any;
    let zipCodes;

    if (!fields) {
      fields = ['name'];
    }

    if (useNewQuery) {
      if (!term && preload) {
        query = {
          bool: {
            must: [
              {
                match_all: {},
              },
            ],
          },
        };
      } else {
        query = {
          bool: {
            must: [],
          },
        };
      }
      // Query with "id" (exact match) OR multi_match (as-you-type) across other fields
      if (term && term !== '') {
        const nonIdFields = fields.filter(field => field !== 'id');
        const shouldQueries: any[] = [];

        if (fields.includes('id')) {
          shouldQueries.push({
            term: {
              id: term,
            },
          });
        }

        if (nonIdFields.length > 0) {
          const keywordFields = ['userType', 'dialerRole'];
          const textFields = nonIdFields.filter(
            f => !keywordFields.includes(f)
          );
          const matchedKeywordFields = nonIdFields.filter(f =>
            keywordFields.includes(f)
          );

          // If the term contains a space, try to match firstName + lastName combinations
          if (
            textFields.includes('firstName') &&
            textFields.includes('lastName') &&
            term.includes(' ')
          ) {
            const [first, ...rest] = term.split(' ');
            const last = rest.join(' ');
            if (first && last) {
              shouldQueries.push({
                bool: {
                  must: [
                    {
                      match_phrase_prefix: {
                        firstName: first,
                      },
                    },
                    {
                      match_phrase_prefix: {
                        lastName: last,
                      },
                    },
                  ],
                },
              });
            }
          }
          if (textFields.length > 0) {
            shouldQueries.push({
              multi_match: {
                query: term,
                type: 'phrase_prefix',
                fields: textFields,
                minimum_should_match: 1,
              },
            });
          }
          for (const kf of matchedKeywordFields) {
            shouldQueries.push({
              prefix: {
                [kf]: { value: term.toLowerCase() },
              },
            });
          }
        }

        if (shouldQueries.length > 0) {
          query.bool.must.push({
            bool: {
              should: shouldQueries,
              minimum_should_match: 1,
            },
          });
        }
      }

      if (filterByAgency) {
        if (!query.bool.must) {
          query.bool.must = [];
        }
        query.bool.must.push({
          term: {
            'agency.id': filterByAgency,
          },
        });
      }

      if (index === 'dialer-agents') {
        query.bool.must.push({
          bool: {
            must: [
              {
                bool: {
                  must_not: {
                    term: {
                      disabled: true,
                    },
                  },
                },
              },
              {
                bool: {
                  should: [
                    {
                      bool: {
                        must_not: {
                          exists: {
                            field: 'disabled.startDate.timestamp',
                          },
                        },
                      },
                    },
                    {
                      range: {
                        'disabled.startDate.timestamp': {
                          gte: new Date().getTime(),
                        },
                      },
                    },
                  ],
                },
              },
            ],
          },
        });
      }

      if (filterIds && filterIds.length > 0) {
        query.bool.must.push({
          terms: {
            _id: filterIds,
          },
        });
      }

      if (filterByTags && filterByTags.length > 0) {
        query.bool.must.push({
          terms: { 'permanentTags.list': filterByTags },
        });
      }
    } else {
      const mustQuery = zipCodes
        ? { terms: { zip: zipCodes } }
        : { match_all: {} };
      query = {
        bool: {
          must: [mustQuery],
        },
      };

      const shouldValues = fields.map((field: string) => ({
        match: {
          [field]: {
            query: term,
            // operator: 'and',
            // fuzziness: 'auto',
          },
        },
      }));

      if (shouldValues.length > 0) {
        query.bool.must.push({
          bool: {
            should: shouldValues,
          },
        });
      }
    }

    let sort;
    if (index === 'queues') {
      sort = [
        {
          // "_score": "desc",
          'name.keyword': 'asc',
        },
      ];
    } else if (index === 'users') {
      sort = [
        {
          userType: 'asc',
        },
        {
          'name.raw': 'asc',
        },
        {
          'firstName.raw': 'asc',
        },
        {
          'lastName.raw': 'asc',
        },
      ];
    }

    try {
      const res = await this.elasticService.query(
        index,
        {
          query,
          sort,
          size,
        },
        undefined,
        true
      );

      const hits: any[] = res['hits']['hits'];

      const searchResult = hits.map(hit => {
        return { ...hit._source, id: hit._id };
      });

      console.log('searchResult', searchResult);
      const suggestions = hits
        .map(hit => {
          if (getFullSource) {
            const result = { ...hit._source };
            if (!result['id']) {
              result['id'] = hit._id;
            }
            return result;
          }
          const res: { [key: string]: any } = {};
          for (const field of fields) {
            res[field] = hit._source[field];
          }
          if (!res['id']) {
            res['id'] = hit._id;
          }
          return res;
        })
        .filter(x => {
          if (
            filterByAgency &&
            x['agency'] &&
            x['agency']['id'] !== filterByAgency
          ) {
            return false;
          }
          return true;
        });

      return { suggestions };
    } catch (error) {
      console.error('Error occurred while performing autocomplete', error);
      return {};
    }
  }

  async getSingle(id: string, index: string) {
    const res: any = await httpsCallable(
      this.functions,
      'getSingleSearch'
    )({ id, index });
    if (res.data.error) {
      console.log('error', res);
      return null;
    }

    return res.data.result;
  }

  async searchUsers(params: UserSearchParams): Promise<UserSearchResult[]> {
    const searchBody: Record<string, unknown> = {
      query: params.query,
      size: params.size || 100,
    };

    if (params.userType && params.userType.length > 0) {
      searchBody['userType'] = params.userType;
    }

    if (params.agencyId) {
      searchBody['agencyId'] = params.agencyId;
    }

    const response = await this.apiService.post<UserSearchResponse>(
      'user/search',
      searchBody
    );

    return response?.users || [];
  }
}
